// src/components/goals/GoalForm.tsx
import React from 'react';
import { Save, X, RefreshCw } from 'lucide-react';
import { GoalData } from '../../pages/Goals'; // Adjust path if necessary
import { LookupValue } from '../../services/lookupService'; // Adjust path

interface GoalFormProps {
  formData: GoalData;
  editingId: string | null;
  isFormSubmitted: boolean;
  dropdownOptions: {
    ownerType: LookupValue[];
    goalType: LookupValue[];
    goalSubType: LookupValue[];
  };
  dependents: { id: string; name: string }[];
  loadingSubTypes: boolean;
  handleSubmit: (e: React.FormEvent) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onCancel: () => void;
}

const renderDropdownOptions = (options: LookupValue[], placeholder = 'Select...') => (
  <>
    <option value="">{placeholder}</option>
    {options && options.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </>
);

const GoalForm: React.FC<GoalFormProps> = ({
  formData, editingId, isFormSubmitted, dropdownOptions, dependents, loadingSubTypes,
  handleSubmit, handleInputChange, handleSelectChange, onCancel
}) => {
  return (
    <div className="p-6 bg-white">
      <h3 className="text-xl font-semibold text-gray-900 mb-4 md:mb-6">{editingId ? 'Edit Goal' : 'Add New Goal'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Owner Type *</label>
            <select name="goalOwnerType" value={formData.goalOwnerType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
              {renderDropdownOptions(dropdownOptions.ownerType, 'Select Owner Type')}
            </select>
          </div>
          {formData.goalOwnerType === '2' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Family Member *</label>
              <select name="familyMemberId" value={formData.familyMemberId} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
                <option value="">Select Family Member</option>
                {dependents.map(dep => <option key={dep.id} value={dep.id}>{dep.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Goal Name *</label>
            <input type="text" name="goalName" value={formData.goalName} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Goal Type *</label>
            <select name="goalType" value={formData.goalType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
              {renderDropdownOptions(dropdownOptions.goalType, 'Select Type')}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sub Type</label>
            <select name="goalSubType" value={formData.goalSubType} onChange={handleSelectChange} className="w-full p-2 border border-ray-300 rounded-lg text-sm" disabled={loadingSubTypes}>
              {loadingSubTypes ? <option>Loading...</option> : renderDropdownOptions(dropdownOptions.goalSubType, formData.goalType ? 'Select Sub Type' : 'Select Type first')}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Target Amount *</label>
            <input type="number" name="goalTarget" value={formData.goalTarget} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tenure (Years)</label>
            <input type="number" name="goalTargetTenure" value={formData.goalTargetTenure} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tentative Amount</label>
            <input type="number" name="goalCompletionTentativeAmount" value={formData.goalCompletionTentativeAmount} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>
        <div className="flex flex-col-reverse md:flex-row justify-end space-y-reverse space-y-2 md:space-y-0 md:space-x-4 mt-6">
          <button type="button" onClick={onCancel} className="bg-red-500 text-white py-2 px-4 md:px-6 md:py-3 rounded-lg hover:bg-red-600 flex items-center justify-center">
            <X className="w-4 h-4 mr-2" />Cancel
          </button>
          <button type="submit" className="px-4 py-2 md:px-6 md:py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center" disabled={isFormSubmitted}>
            {isFormSubmitted ? <RefreshCw className="w-4 h-4 md:w-5 md:h-5 mr-2 animate-spin" /> : <Save className="w-4 h-4 md:w-5 md:h-5 mr-2" />}
            {isFormSubmitted ? 'Loading' : editingId ? 'Update Goal' : 'Add Goal'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GoalForm;